import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { DropdownCoordinatorService } from '@app/storybook/components/dropdown/dropdown-coordinator.service';
import { EditProductModalComponent, EditProductModalResult } from '../edit-product-modal/edit-product-modal.component';

const ANY_VARIANT_LABEL = 'All variants';

export interface ProductOption {
  name: string;
  values: string[];
}

export interface SelectableProductVariant {
  id: string | number;
  title: string;
  price?: string;
}

export interface SelectableProduct {
  id: string | number;
  handle?: string;
  title: string;
  sku?: string;
  price?: string;
  imageUrl?: string;
  options?: ProductOption[];
  selectedOptions?: ProductOption[];
  selectableVariants?: SelectableProductVariant[];
  variantId?: string;
  variantTitle?: string;
  allowedSellingPlans?: string[];
  displayName?: string;
  displayDescription?: string;
  [key: string]: any;
}

export interface AddProductsModalConfig {
  title?: string;
  searchPlaceholder?: string;
  addButtonText?: string;
  excludeProductIds?: (string | number)[];
  enableVariants?: boolean;
  enableEdit?: boolean;
  allowEmptySelection?: boolean;
  enableProductEdit?: boolean;
  openProductInShopify?: (product: SelectableProduct) => void;
  preSelectedProducts?: SelectableProduct[];
  lockedProductIds?: (string | number)[];
  canChooseOnlyOneProduct?: boolean;
  fetchProducts?: (query: string) => Promise<SelectableProduct[]>;
  isMultiSelect?: boolean;
  hideSelectAll?: boolean;
  enableSpecificVariantSelection?: boolean;
  enableSellingPlans?: boolean;
  fetchSellingPlans?: (productId: string | number) => Promise<{ label: string; value: string; checked: boolean }[]>;
}

@Component({
  selector: 'add-products-modal',
  templateUrl: './add-products-modal.component.html',
  styleUrls: ['./add-products-modal.component.scss'],
  standalone: false,
  providers: [DropdownCoordinatorService],
})
export class AddProductsModalComponent implements OnInit, OnDestroy {
  title = 'Select Products';
  searchPlaceholder = 'Search product by name or SKU';
  addButtonText = 'Save';

  searchQuery = '';
  products: SelectableProduct[] = [];
  selectedProductIds: Set<string | number> = new Set();
  isLoading = false;
  enableVariants = false;
  enableEdit = false;
  allowEmptySelection = false;
  enableProductEdit = false;
  canOpenInShopify = false;
  canChooseOnlyOneProduct = false;
  isMultiSelect = true;
  hideSelectAll = false;
  variantSelections: Map<string | number, ProductOption[]> = new Map();
  variantDropdownOptions: Map<string | number, Map<string, { label: string; value: string; checked: boolean }[]>> =
    new Map();
  enableSellingPlans = false;
  enableSpecificVariantSelection = false;
  specificVariantIdByProductId: Map<string | number, string> = new Map();
  loadingSellingPlans: Set<string | number> = new Set();
  sellingPlanSelections: Map<string | number, string[]> = new Map();
  sellingPlanDropdownOptions: Map<string | number, { label: string; value: string; checked: boolean }[]> = new Map();

  private fetchSellingPlansFn:
    | ((productId: string | number) => Promise<{ label: string; value: string; checked: boolean }[]>)
    | null = null;
  private fetchProducts: ((query: string) => Promise<SelectableProduct[]>) | null = null;
  private openProductInShopifyFn: ((product: SelectableProduct) => void) | null = null;
  private productOverrides: Map<string | number, EditProductModalResult> = new Map();
  // restores choices when a product is unticked and ticked again in the same session
  private rememberedSellingPlanSelections: Map<string | number, string[]> = new Map();
  private rememberedVariantSelections: Map<string | number, ProductOption[]> = new Map();
  private touchedSellingPlanProductIds: Set<string | number> = new Set();
  private excludeProductIds: Set<string | number> = new Set();
  private lockedProductIds: Set<string | number> = new Set();
  private preSelectedProducts: SelectableProduct[] = [];
  private allLoadedProducts: Map<string | number, SelectableProduct> = new Map();
  private searchSubject$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AddProductsModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddProductsModalConfig,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private dropdownCoordinator: DropdownCoordinatorService,
  ) {}

  ngOnInit(): void {
    if (this.data?.title) {
      this.title = this.data.title;
    }
    if (this.data?.searchPlaceholder) {
      this.searchPlaceholder = this.data.searchPlaceholder;
    }
    if (this.data?.addButtonText) {
      this.addButtonText = this.data.addButtonText;
    }
    if (this.data?.fetchProducts) {
      this.fetchProducts = this.data.fetchProducts;
    }

    if (this.data?.enableVariants !== undefined) {
      this.enableVariants = this.data.enableVariants;
    }
    if (this.data?.enableSpecificVariantSelection !== undefined) {
      this.enableSpecificVariantSelection = this.data.enableSpecificVariantSelection;
    }
    if (this.data?.enableEdit !== undefined) {
      this.enableEdit = this.data.enableEdit;
    }
    if (this.data?.allowEmptySelection !== undefined) {
      this.allowEmptySelection = this.data.allowEmptySelection;
    }
    if (this.data?.enableProductEdit !== undefined) {
      this.enableProductEdit = this.data.enableProductEdit;
    }
    if (this.data?.openProductInShopify) {
      this.openProductInShopifyFn = this.data.openProductInShopify;
      this.canOpenInShopify = true;
    }
    if (this.data?.preSelectedProducts) {
      this.preSelectedProducts = this.data.preSelectedProducts;
      this.preSelectedProducts.forEach((preSelectedProduct) => {
        this.allLoadedProducts.set(preSelectedProduct.id, preSelectedProduct);
        // select upfront — a saved product not in the current page would be dropped on save
        this.selectedProductIds.add(preSelectedProduct.id);
        this.productOverrides.set(preSelectedProduct.id, {
          displayName: preSelectedProduct.displayName,
          displayDescription: preSelectedProduct.displayDescription,
        });
        if (this.enableSpecificVariantSelection && preSelectedProduct.variantId) {
          this.specificVariantIdByProductId.set(preSelectedProduct.id, String(preSelectedProduct.variantId));
        }
        if (this.enableVariants && preSelectedProduct.selectedOptions) {
          this.variantSelections.set(
            preSelectedProduct.id,
            preSelectedProduct.selectedOptions.map((option) => ({ ...option })),
          );
        }
      });
    }
    if (this.data?.lockedProductIds) {
      this.data.lockedProductIds.forEach((id) => this.lockedProductIds.add(id));
    }
    if (this.data?.canChooseOnlyOneProduct !== undefined) {
      this.canChooseOnlyOneProduct = this.data.canChooseOnlyOneProduct;
    }
    if (this.data?.excludeProductIds) {
      this.data.excludeProductIds.forEach((id) => this.excludeProductIds.add(id));
    }
    if (this.data?.isMultiSelect !== undefined) {
      this.isMultiSelect = this.data.isMultiSelect;
    }
    if (this.data?.hideSelectAll !== undefined) {
      this.hideSelectAll = this.data.hideSelectAll;
    }
    if (this.data?.enableSellingPlans !== undefined) {
      this.enableSellingPlans = this.data.enableSellingPlans;
    }
    if (this.data?.fetchSellingPlans) {
      this.fetchSellingPlansFn = this.data.fetchSellingPlans;
    }
    if (this.enableSellingPlans) {
      this.preSelectedProducts.forEach((product) => this.loadSellingPlansForProduct(product));
    }

    this.searchSubject$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe((query) => {
      this.loadProducts(query);
    });

    this.loadProducts('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadProducts(query: string): Promise<void> {
    if (!this.fetchProducts) {
      return;
    }

    this.isLoading = true;
    try {
      const products = await this.fetchProducts(query);
      this.products = this.enableEdit ? products : products.filter((p) => !this.excludeProductIds.has(p.id));
      this.products.forEach((p) => this.allLoadedProducts.set(p.id, p));
      this.wireVariantDropdownsForPreSelectedProducts();
    } catch (error) {
      console.error('Error fetching products:', error);
      this.products = [];
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchSubject$.next(query);
  }

  isSelected(productId: string | number): boolean {
    return this.selectedProductIds.has(productId) || this.lockedProductIds.has(productId);
  }

  // Ticked but never returned — locked products belong to another configuration.
  isLocked(productId: string | number): boolean {
    return this.lockedProductIds.has(productId);
  }

  onEditProduct(product: SelectableProduct): void {
    const override = this.productOverrides.get(product.id);
    this.dialog
      .open(EditProductModalComponent, {
        data: {
          product: {
            id: product.id,
            name: product.title,
            description: product.description,
            price: product.price,
            sku: product.sku,
            imageUrl: product.imageUrl,
            displayName: override?.displayName ?? '',
            displayDescription: override?.displayDescription ?? '',
          },
        },
        width: '650px',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: EditProductModalResult | undefined) => {
        if (result) this.productOverrides.set(product.id, result);
      });
  }

  onOpenInShopify(product: SelectableProduct): void {
    this.openProductInShopifyFn?.(product);
  }

  // first click only closes an open dropdown, it doesn't toggle the row
  onRowClick(product: SelectableProduct): void {
    if (this.dropdownCoordinator.closeOpenDropdown()) return;
    this.toggleProduct(product);
  }

  toggleProduct(product: SelectableProduct): void {
    if (this.lockedProductIds.has(product.id)) {
      return;
    }

    // closing on click would skip the variant dropdown, which only renders once the product is selected
    if (!this.isMultiSelect && !this.enableSpecificVariantSelection) {
      this.dialogRef.close([product]);
      return;
    }

    if (this.selectedProductIds.has(product.id)) {
      this.clearProductSelection(product.id);
      return;
    }

    if (this.canChooseOnlyOneProduct || !this.isMultiSelect) {
      this.selectedProductIds.clear();
      this.variantSelections.clear();
      this.variantDropdownOptions.clear();
      this.specificVariantIdByProductId.clear();
    }

    this.setProductSelected(product);
  }

  private setProductSelected(product: SelectableProduct): void {
    this.selectedProductIds.add(product.id);
    if (this.enableSellingPlans) this.loadSellingPlansForProduct(product);
    if (!this.enableVariants || !this.hasRealVariants(product)) return;
    const isSingleSelect = this.canChooseOnlyOneProduct;
    const remembered = this.rememberedVariantSelections.get(product.id);
    const selectedValuesFor = (opt: ProductOption): string[] =>
      remembered ? (remembered.find((r) => r.name === opt.name)?.values ?? []) : isSingleSelect ? [] : [...opt.values];
    this.variantSelections.set(
      product.id,
      product.options.map((opt) => ({ name: opt.name, values: selectedValuesFor(opt) })),
    );
    const optionsMap = new Map<string, { label: string; value: string; checked: boolean }[]>();
    product.options.forEach((opt) => {
      const selectedValues = selectedValuesFor(opt);
      optionsMap.set(
        opt.name,
        opt.values.map((v) => ({ label: v, value: v, checked: selectedValues.includes(v) })),
      );
    });
    this.variantDropdownOptions.set(product.id, optionsMap);
  }

  // Mirror of setProductSelected() — keeps all maps in sync on removal.
  private clearProductSelection(productId: string | number): void {
    const variantSelection = this.variantSelections.get(productId);
    if (variantSelection) {
      this.rememberedVariantSelections.set(
        productId,
        variantSelection.map((option) => ({ ...option, values: [...option.values] })),
      );
    }
    const sellingPlanSelection = this.sellingPlanSelections.get(productId);
    if (sellingPlanSelection) this.rememberedSellingPlanSelections.set(productId, [...sellingPlanSelection]);
    this.selectedProductIds.delete(productId);
    this.specificVariantIdByProductId.delete(productId);
    this.variantSelections.delete(productId);
    this.variantDropdownOptions.delete(productId);
    this.sellingPlanSelections.delete(productId);
    this.sellingPlanDropdownOptions.delete(productId);
    this.loadingSellingPlans.delete(productId);
  }

  private async loadSellingPlansForProduct(product: SelectableProduct): Promise<void> {
    if (!this.fetchSellingPlansFn) return;
    this.loadingSellingPlans.add(product.id);
    this.cdr.detectChanges();
    const savedPlans = this.rememberedSellingPlanSelections.get(product.id) ?? product.allowedSellingPlans;
    // empty or missing saved plans mean one-time only in checkout, not "all plans"
    const showSavedPlans = savedPlans !== undefined || this.isPreSelected(product.id);
    try {
      const options = (await this.fetchSellingPlansFn(product.id)) ?? [];
      if (!this.selectedProductIds.has(product.id)) return;
      const planOptions = options.map((o) => ({
        ...o,
        checked: showSavedPlans ? (savedPlans ?? []).includes(o.value) : o.checked,
      }));
      this.sellingPlanDropdownOptions.set(product.id, planOptions);
      this.sellingPlanSelections.set(
        product.id,
        planOptions.filter((o) => o.checked).map((o) => o.value),
      );
    } catch {
      this.sellingPlanDropdownOptions.set(product.id, []);
      // otherwise a failed fetch wipes the saved plans on save
      this.sellingPlanSelections.set(product.id, savedPlans ?? []);
    } finally {
      this.loadingSellingPlans.delete(product.id);
      this.cdr.detectChanges();
    }
  }

  isLoadingSellingPlans(productId: string | number): boolean {
    return this.loadingSellingPlans.has(productId);
  }

  getSellingPlanOptions(productId: string | number): { label: string; value: string; checked: boolean }[] {
    return this.sellingPlanDropdownOptions.get(productId) ?? [];
  }

  getSellingPlanDefaultOptions(productId: string | number): { label: string; value: string; checked: boolean }[] {
    return (this.sellingPlanDropdownOptions.get(productId) ?? []).filter((o) => o.checked);
  }

  onSellingPlanSelectionChange(
    productId: string | number,
    options: { label: string; value: string; checked: boolean }[],
  ): void {
    this.touchedSellingPlanProductIds.add(productId);
    this.sellingPlanDropdownOptions.set(productId, options);
    this.sellingPlanSelections.set(
      productId,
      options.filter((o) => o.checked).map((o) => o.value),
    );
  }

  getSpecificVariantOptions(product: SelectableProduct): { label: string; value: string }[] {
    return [
      { label: ANY_VARIANT_LABEL, value: '' },
      ...(product.selectableVariants ?? []).map((variant) => ({
        label: variant.price ? `${variant.title} — $${variant.price}` : variant.title,
        value: String(variant.id),
      })),
    ];
  }

  getSpecificVariantDefaultOption(product: SelectableProduct): { label: string; value: string } {
    const selectedVariantId = this.specificVariantIdByProductId.get(product.id);
    return (
      this.getSpecificVariantOptions(product).find((option) => option.value === (selectedVariantId ?? '')) ?? {
        label: ANY_VARIANT_LABEL,
        value: '',
      }
    );
  }

  onSpecificVariantChange(productId: string | number, selectedOption: { value: string } | null): void {
    if (selectedOption?.value) {
      this.specificVariantIdByProductId.set(productId, selectedOption.value);
      return;
    }
    this.specificVariantIdByProductId.delete(productId);
  }

  hasSelectableVariants(product: SelectableProduct): boolean {
    return (product.selectableVariants?.length ?? 0) > 1;
  }

  hasRealVariants(product: SelectableProduct): boolean {
    if (!product.options || product.options.length === 0) return false;
    if (
      product.options.length === 1 &&
      product.options[0].values.length === 1 &&
      product.options[0].values[0] === 'Default Title'
    ) {
      return false;
    }
    return true;
  }

  getVariantDropdownOptions(
    productId: string | number,
    optionName: string,
  ): { label: string; value: string; checked: boolean }[] {
    return this.variantDropdownOptions.get(productId)?.get(optionName) ?? [];
  }

  getVariantDefaultOptions(
    productId: string | number,
    optionName: string,
  ): { label: string; value: string; checked: boolean }[] {
    return (this.variantDropdownOptions.get(productId)?.get(optionName) ?? []).filter((o) => o.checked);
  }

  onVariantSelectionChange(
    productId: string | number,
    optionName: string,
    options: { label: string; value: string; checked: boolean }[],
  ): void {
    const selections = this.variantSelections.get(productId);
    if (!selections) return;
    const optionEntry = selections.find((s) => s.name === optionName);
    if (optionEntry) {
      optionEntry.values = options.filter((o) => o.checked).map((o) => o.value);
    }
  }

  onSingleVariantSelectionChange(
    productId: string | number,
    optionName: string,
    selectedOption: { label: string; value: string } | null,
  ): void {
    const selections = this.variantSelections.get(productId);
    if (!selections) return;
    const optionEntry = selections.find((s) => s.name === optionName);
    if (optionEntry) {
      optionEntry.values = selectedOption?.value ? [selectedOption.value] : [];
    }
    const optionsMap = this.variantDropdownOptions.get(productId);
    if (optionsMap) {
      const options = optionsMap.get(optionName);
      options?.forEach((o) => (o.checked = o.value === selectedOption?.value));
    }
  }

  getVariantSingleDefaultOption(
    productId: string | number,
    optionName: string,
  ): { label: string; value: string } | null {
    const options = this.variantDropdownOptions.get(productId)?.get(optionName);
    if (!options) return null;
    const selected = options.find((o) => o.checked);
    return selected ? { label: selected.label, value: selected.value } : null;
  }

  get selectedCount(): number {
    return this.selectedProductIds.size;
  }

  get isSelectDisabled(): boolean {
    if (!this.selectedCount) return !this.allowEmptySelection;
    if (this.enableSellingPlans) {
      for (const productId of this.selectedProductIds) {
        if (this.loadingSellingPlans.has(productId)) return true;
        const hasPlanOptions = (this.sellingPlanDropdownOptions.get(productId) ?? []).length > 0;
        if (
          hasPlanOptions &&
          !this.sellingPlanSelections.get(productId)?.length &&
          !this.hasUntouchedSavedPlans(productId)
        )
          return true;
      }
    }
    if (!this.enableVariants) return false;
    for (const productId of this.selectedProductIds) {
      const product = this.allLoadedProducts.get(productId);
      if (!product || !this.hasRealVariants(product)) continue;
      const selections = this.variantSelections.get(productId);
      if (!selections || selections.some((opt) => !opt.values.length)) return true;
    }
    return false;
  }

  private get selectableProducts(): SelectableProduct[] {
    return this.products.filter((p) => !this.lockedProductIds.has(p.id));
  }

  get allSelected(): boolean {
    const { selectableProducts } = this;
    return selectableProducts.length > 0 && selectableProducts.every((p) => this.selectedProductIds.has(p.id));
  }

  toggleSelectAll(): void {
    if (this.canChooseOnlyOneProduct) return;

    const { selectableProducts } = this;
    if (this.allSelected) {
      selectableProducts.forEach((p) => this.clearProductSelection(p.id));
      return;
    }
    selectableProducts.forEach((p) => {
      if (this.selectedProductIds.has(p.id)) return;
      this.setProductSelected(p);
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAddProducts(): void {
    const selectedProducts: SelectableProduct[] = [];
    for (const productId of this.selectedProductIds) {
      const product = this.allLoadedProducts.get(productId);
      if (!product) continue;
      const selections = this.variantSelections.get(productId);
      const selectedProduct: SelectableProduct = selections
        ? { ...product, selectedOptions: selections }
        : { ...product };
      if (this.enableProductEdit) {
        const override = this.productOverrides.get(productId);
        selectedProduct.displayName = override?.displayName;
        selectedProduct.displayDescription = override?.displayDescription;
      }
      if (this.enableSellingPlans) {
        selectedProduct.allowedSellingPlans = this.hasUntouchedSavedPlans(productId)
          ? this.preSelectedProducts.find((p) => p.id === productId)?.allowedSellingPlans
          : (this.sellingPlanSelections.get(productId) ?? []);
      }
      if (this.enableSpecificVariantSelection) {
        const selectedVariantId = this.specificVariantIdByProductId.get(productId);
        const selectedVariant = product.selectableVariants?.find((variant) => String(variant.id) === selectedVariantId);
        selectedProduct.variantId = selectedVariantId;
        selectedProduct.variantTitle = selectedVariantId ? (selectedVariant?.title ?? product.variantTitle) : undefined;
        selectedProduct.price = selectedVariantId ? (selectedVariant?.price ?? product.price) : product.price;
      }
      selectedProducts.push(selectedProduct);
    }
    this.dialogRef.close(selectedProducts);
  }

  private isPreSelected(productId: string | number): boolean {
    return this.preSelectedProducts.some((product) => product.id === productId);
  }

  private hasUntouchedSavedPlans(productId: string | number): boolean {
    return this.isPreSelected(productId) && !this.touchedSellingPlanProductIds.has(productId);
  }

  // never re-select here — a deselected product would come back on the next search
  private wireVariantDropdownsForPreSelectedProducts(): void {
    if (!this.enableVariants || !this.preSelectedProducts.length) return;
    this.preSelectedProducts.forEach((preSelected) => {
      if (this.variantDropdownOptions.has(preSelected.id)) return;
      // no saved choice means all variants; single-select still needs a pick
      if (
        !preSelected.selectedOptions &&
        (this.canChooseOnlyOneProduct || !this.selectedProductIds.has(preSelected.id))
      )
        return;
      const product = this.products.find((p) => p.id === preSelected.id);
      if (!product || !this.hasRealVariants(product)) return;
      const optionsMap = new Map<string, { label: string; value: string; checked: boolean }[]>();
      product.options.forEach((opt) => {
        const savedValues = new Set(
          preSelected.selectedOptions?.find((so) => so.name === opt.name)?.values ?? opt.values,
        );
        optionsMap.set(
          opt.name,
          opt.values.map((v) => ({ label: v, value: v, checked: savedValues.has(v) })),
        );
      });
      this.variantDropdownOptions.set(product.id, optionsMap);
      if (!preSelected.selectedOptions) {
        this.variantSelections.set(
          product.id,
          product.options.map((opt) => ({ name: opt.name, values: [...opt.values] })),
        );
      }
    });
  }
}
